import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Image, ActivityIndicator, FlatList, RefreshControl, Alert, Linking, Dimensions,
  Modal, TextInput, Animated, Share, Platform,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const { width: SW } = Dimensions.get('window');

// ═══════════════════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const APPS_KEY = 'job_applications_data';

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS CONFIG
// ═══════════════════════════════════════════════════════════════════════════════
const STATUS = {
  draft:        {label:'Draft',           color:'#94A3B8',icon:'create-outline',    bg:'#F1F5F9',progress:5},
  submitted:    {label:'Submitted',       color:'#3B82F6',icon:'send-outline',      bg:'#DBEAFE',progress:15},
  received:     {label:'Received',        color:'#6366F1',icon:'mail-open-outline', bg:'#E0E7FF',progress:20},
  under_review: {label:'Under Review',    color:'#F59E0B',icon:'eye-outline',       bg:'#FEF3C7',progress:30},
  screening:    {label:'Screening',       color:'#8B5CF6',icon:'filter-outline',    bg:'#EDE9FE',progress:35},
  shortlisted:  {label:'Shortlisted',     color:'#06B6D4',icon:'star-outline',      bg:'#CFFAFE',progress:45},
  assessment:   {label:'Assessment',      color:'#D946EF',icon:'school-outline',    bg:'#FAE8FF',progress:50},
  interview_scheduled:{label:'Interview',  color:'#0EA5E9',icon:'calendar-outline', bg:'#E0F2FE',progress:60},
  interview_completed:{label:'Interviewed',color:'#14B8A6',icon:'checkmark-circle-outline',bg:'#CCFBF1',progress:70},
  offer:        {label:'Offer Received',  color:'#10B981',icon:'gift-outline',      bg:'#D1FAE5',progress:90},
  accepted:     {label:'Accepted',        color:'#059669',icon:'checkmark-done',    bg:'#A7F3D0',progress:100},
  hired:        {label:'Hired',           color:'#059669',icon:'trophy-outline',    bg:'#A7F3D0',progress:100},
  rejected:     {label:'Rejected',        color:'#EF4444',icon:'close-circle-outline',bg:'#FEE2E2',progress:0},
  withdrawn:    {label:'Withdrawn',       color:'#94A3B8',icon:'exit-outline',      bg:'#F1F5F9',progress:0},
  active:       {label:'Active',          color:'#3B82F6',icon:'pulse-outline',     bg:'#DBEAFE',progress:25},
  pending:      {label:'Pending',         color:'#F59E0B',icon:'time-outline',      bg:'#FEF3C7',progress:20},
};

const sc = (status) => STATUS[status] || STATUS[(status||'').toLowerCase().replace(/\s+/g,'_')] || {label:status||'Unknown',color:'#94A3B8',icon:'help-circle-outline',bg:'#F1F5F9',progress:0};

const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}); } catch { return d||'N/A'; } };
const fmtDateTime = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}); } catch { return d||'N/A'; } };

const FILTER_TABS = [
  {key:'all',        label:'All'},
  {key:'active',     label:'Active'},
  {key:'interviews', label:'Interviews'},
  {key:'offers',     label:'Offers'},
  {key:'closed',     label:'Closed'},
];

const SORT_OPTS = [
  {key:'date',    label:'Date Applied',   icon:'calendar-outline'},
  {key:'company', label:'Company',        icon:'business-outline'},
  {key:'status',  label:'Status Progress',icon:'trending-up-outline'},
  {key:'title',   label:'Job Title',      icon:'text-outline'},
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function JobApplicationsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const navigation = useNavigation();
  const route = useRoute();

  const [isLoading, setIsLoading]       = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [applications, setApplications] = useState([]);
  const [searchQuery, setSearchQuery]   = useState('');
  const [filterType, setFilterType]     = useState('all');
  const [sortBy, setSortBy]             = useState('date');
  const [showDetail, setShowDetail]     = useState(false);
  const [selectedApp, setSelectedApp]   = useState(null);
  const [showFilters, setShowFilters]   = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [error, setError]               = useState(null);

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ═════════════════════════════════════════════════════════════════════════════
  // LOAD APPLICATIONS — merged from API + local storage
  // ═════════════════════════════════════════════════════════════════════════════
  const loadApplications = useCallback(async () => {
    setError(null);
    try {
      let apps = [];

      // 1. Load from API — filter by the logged-in user's email so the list
      //    shows only THEIR applications, not every application in the system.
      let myEmail = '';
      try {
        const profRaw = await AsyncStorage.getItem('prof_user_profile');
        const prof = profRaw ? JSON.parse(profRaw) : {};
        myEmail = (prof.personal?.email || prof.email || '').toLowerCase();
      } catch {}
      if (!myEmail) {
        try {
          const authRaw = await AsyncStorage.getItem('userData') || await AsyncStorage.getItem('user');
          const auth = authRaw ? JSON.parse(authRaw) : {};
          myEmail = (auth.email || auth.user?.email || '').toLowerCase();
        } catch {}
      }
      console.log('📧 JobApplications loading for email:', myEmail || '(none)');

      // Use the candidate-scoped JOINed endpoint when we know the email.
      // Falls back to the generic recruiter-side route only as a last resort.
      const apiResp = myEmail
        ? await apiFetch(`/api/professional/my-applications?email=${encodeURIComponent(myEmail)}&limit=100`)
        : await apiFetch('/api/data/applications?sort=applied_date&order=desc&limit=50');
      // Server returns { success, data: [...] } OR a bare array — handle both
      const apiApps = Array.isArray(apiResp)
        ? apiResp
        : (apiResp?.data || apiResp?.applications || []);
      console.log(`✅ Applications from API: ${apiApps.length}`);

      if (Array.isArray(apiApps) && apiApps.length > 0) {
        apps = apiApps.map(a => ({
          id:          String(a.id),
          jobId:       a.job_id || a.jobId || '',
          // Order matters: job_title (from JOIN with jobs table) is the job
          // applied to. current_title is the candidate's CURRENT role and
          // should only be a last-ditch fallback.
          jobTitle:    a.job_title || a.position || a.title || a.current_title || 'Job Application',
          company:     a.employer_name || a.company_name || a.company || a.employer || '',
          companyLogo: a.employer_logo || a.company_logo || a.logo || null,
          // Prefer the job's location (from JOIN) over generic location.
          location:    a.job_location || a.location || a.candidate_location || '',
          salary:
            (a.job_salary_min || a.job_salary_max)
              ? [
                  a.job_salary_min ? `£${Number(a.job_salary_min).toLocaleString()}` : '',
                  a.job_salary_max ? `£${Number(a.job_salary_max).toLocaleString()}` : '',
                ].filter(Boolean).join(' – ')
              : (a.salary || ''),
          jobType:     a.job_type || a.jobType || 'Full-time',
          status:      (a.status||'submitted').toLowerCase().replace(/\s+/g,'_'),
          stage:       a.stage || '',
          appliedDate: a.applied_date || a.created_at || new Date().toISOString(),
          lastUpdated: a.updated_at || a.created_at || '',
          matchScore:  a.match_score || a.ai_score || a.overall_score || 0,
          isRemote:    !!(a.job_is_remote || a.is_remote),
          // Candidate identity from JOIN (real full_name from users table)
          candidateName:  a.candidate_name || '',
          candidateAvatar: a.candidate_avatar || null,
          candidateEmail: a.candidate_email || '',
          // Recruiter-side fields (still optional, may not be on the new endpoint)
          recruiterName:  a.recruiter_name || '',
          recruiterEmail: a.recruiter_email || '',
          recruiterPhone: a.recruiter_phone || '',
          interviewDate:  a.interview_date || null,
          interviewType:  a.interview_type || '',
          source:      'api',
        }));
      }

      // 2. Load local drafts/submissions
      const localRaw = await AsyncStorage.getItem(APPS_KEY);
      if (localRaw) {
        const localApps = JSON.parse(localRaw);
        if (Array.isArray(localApps)) {
          const apiIds = new Set(apps.map(a=>a.id));
          const localOnly = localApps
            .filter(la => !apiIds.has(String(la.id)))
            .map(la => ({
              id:          String(la.id),
              jobId:       la.jobId || '',
              jobTitle:    la.jobTitle || 'Job Application',
              company:     la.company || '',
              location:    la.location || '',
              salary:      la.salary || '',
              jobType:     la.jobType || 'Full-time',
              status:      (la.status||'draft').toLowerCase().replace(/\s+/g,'_'),
              appliedDate: la.appliedDate || la.createdAt || new Date().toISOString(),
              lastUpdated: la.lastUpdated || '',
              matchScore:  la.matchScore || 0,
              statusHistory: la.statusHistory || [],
              source:      'local',
            }));
          apps = [...apps, ...localOnly];
        }
      }

      // Cache API results locally
      if (apps.length > 0) {
        await AsyncStorage.setItem(`prof_cached_applications`, JSON.stringify(apps));
      }

      setApplications(apps);
    } catch(e) {
      setError('Failed to load applications. Pull to retry.');
      // Try cached
      const cached = await AsyncStorage.getItem('prof_cached_applications');
      if (cached) setApplications(JSON.parse(cached));
    }
  }, []);

  useFocusEffect(useCallback(() => {
    loadApplications().finally(()=>setIsLoading(false));
  }, []));

  useEffect(() => {
    Animated.timing(fadeAnim,{toValue:1,duration:600,useNativeDriver:true}).start();
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadApplications();
    setRefreshing(false);
  }, [loadApplications]);

  // ═════════════════════════════════════════════════════════════════════════════
  // FILTERING & SORTING
  // ═════════════════════════════════════════════════════════════════════════════
  const filtered = useMemo(() => {
    let list = [...applications];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(a =>
        (a.jobTitle||'').toLowerCase().includes(q) ||
        (a.company||'').toLowerCase().includes(q) ||
        (a.location||'').toLowerCase().includes(q)
      );
    }

    // Filter
    if (filterType !== 'all') {
      list = list.filter(a => {
        const st = a.status;
        switch(filterType) {
          case 'active':
            return ['submitted','received','under_review','screening','shortlisted','assessment','active','pending'].includes(st);
          case 'interviews':
            return ['interview_scheduled','interview_completed'].includes(st);
          case 'offers':
            return ['offer','accepted'].includes(st);
          case 'closed':
            return ['hired','rejected','withdrawn'].includes(st);
          default: return true;
        }
      });
    }

    // Sort
    list.sort((a,b) => {
      switch(sortBy) {
        case 'company': return (a.company||'').localeCompare(b.company||'');
        case 'title':   return (a.jobTitle||'').localeCompare(b.jobTitle||'');
        case 'status':  return (sc(b.status).progress||0) - (sc(a.status).progress||0);
        default:        return new Date(b.appliedDate||0) - new Date(a.appliedDate||0);
      }
    });

    return list;
  }, [applications, searchQuery, filterType, sortBy]);

  // ═════════════════════════════════════════════════════════════════════════════
  // STATS
  // ═════════════════════════════════════════════════════════════════════════════
  const stats = useMemo(() => {
    const total = applications.length;
    const active = applications.filter(a=>['submitted','received','under_review','screening','shortlisted','assessment','active','pending'].includes(a.status)).length;
    const interviews = applications.filter(a=>['interview_scheduled','interview_completed'].includes(a.status)).length;
    const offers = applications.filter(a=>['offer','accepted'].includes(a.status)).length;
    const rejected = applications.filter(a=>a.status==='rejected').length;
    const rate = total>0 ? Math.round(((interviews+offers)/(total-rejected||1))*100) : 0;
    return {total,active,interviews,offers,rejected,rate};
  }, [applications]);

  // ═════════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═════════════════════════════════════════════════════════════════════════════
  const withdrawApp = useCallback((app) => {
    Alert.alert('Withdraw Application',`Withdraw your application for ${app.jobTitle}?`,[
      {text:'Cancel',style:'cancel'},
      {text:'Withdraw',style:'destructive',onPress:async()=>{
        try {
          await apiFetch(`/api/data/applications/${app.id}`,{method:'PUT',body:JSON.stringify({status:'withdrawn'})});
          // Update local
          const raw = await AsyncStorage.getItem(APPS_KEY);
          if (raw) {
            let local = JSON.parse(raw);
            local = local.map(a=>a.id===app.id?{...a,status:'withdrawn'}:a);
            await AsyncStorage.setItem(APPS_KEY, JSON.stringify(local));
          }
          setShowDetail(false);
          loadApplications();
          Alert.alert('Withdrawn','Application withdrawn.');
        } catch { Alert.alert('Error','Could not withdraw.'); }
      }},
    ]);
  }, [loadApplications]);

  const shareApp = useCallback(async (app) => {
    try {
      await Share.share({message:`I applied for ${app.jobTitle} at ${app.company}. Status: ${sc(app.status).label}`});
    } catch {}
  }, []);

  const deleteApp = useCallback((app) => {
    if (app.source!=='local' && app.status!=='draft') return;
    Alert.alert('Delete Draft',`Delete this draft application?`,[
      {text:'Cancel',style:'cancel'},
      {text:'Delete',style:'destructive',onPress:async()=>{
        const raw = await AsyncStorage.getItem(APPS_KEY);
        if (raw) {
          let local = JSON.parse(raw).filter(a=>a.id!==app.id);
          await AsyncStorage.setItem(APPS_KEY, JSON.stringify(local));
        }
        setShowDetail(false);
        loadApplications();
      }},
    ]);
  }, [loadApplications]);

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: STATS BAR
  // ═════════════════════════════════════════════════════════════════════════════
  const renderStats = () => (
    <View style={st.statsRow}>
      {[
        {label:'Total',    value:stats.total,      color:'#0e4e96'},
        {label:'Active',   value:stats.active,     color:'#3B82F6'},
        {label:'Interviews',value:stats.interviews,color:'#F59E0B'},
        {label:'Offers',   value:stats.offers,     color:'#10B981'},
        {label:'Rate',     value:`${stats.rate}%`,  color:'#8B5CF6'},
      ].map(s=>(
        <View key={s.label} style={st.statCard}>
          <Text style={[st.statNum,{color:s.color}]}>{s.value}</Text>
          <Text style={st.statLbl}>{s.label}</Text>
        </View>
      ))}
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: APPLICATION CARD
  // ═════════════════════════════════════════════════════════════════════════════
  const renderCard = useCallback(({ item }) => {
    const cfg = sc(item.status);
    return (
      <TouchableOpacity style={st.card} onPress={()=>{setSelectedApp(item);setShowDetail(true);}} activeOpacity={0.85}>
        {/* Header */}
        <View style={st.cardTop}>
          <View style={[st.cardLogo,{backgroundColor:cfg.bg}]}>
            <Text style={{fontSize:18,fontWeight:'800',color:cfg.color}}>{(item.company||'?')[0]}</Text>
          </View>
          <View style={{flex:1}}>
            <Text style={st.cardTitle} numberOfLines={1}>{item.jobTitle}</Text>
            <Text style={st.cardCo} numberOfLines={1}>{item.company}</Text>
            <View style={{flexDirection:'row',alignItems:'center',gap:4,marginTop:2}}>
              {item.location ? <><Ionicons name="location-outline" size={11} color="#94A3B8"/><Text style={st.cardMeta}>{item.location}</Text></> : null}
              {item.jobType ? <Text style={st.cardMeta}> • {item.jobType}</Text> : null}
              {item.isRemote ? <View style={st.remotePill}><Text style={st.remotePillT}>Remote</Text></View> : null}
            </View>
          </View>
          <View style={[st.statusBadge,{backgroundColor:cfg.bg}]}>
            <Ionicons name={cfg.icon} size={12} color={cfg.color}/>
            <Text style={[st.statusBadgeT,{color:cfg.color}]}>{cfg.label}</Text>
          </View>
        </View>

        {/* Progress */}
        <View style={st.progressRow}>
          <View style={st.progressBar}>
            <Animated.View style={[st.progressFill,{width:`${cfg.progress}%`,backgroundColor:cfg.color}]}/>
          </View>
          <Text style={[st.progressPct,{color:cfg.color}]}>{cfg.progress}%</Text>
        </View>

        {/* Footer */}
        <View style={st.cardFoot}>
          <View style={{flex:1}}>
            {item.salary ? <Text style={st.cardSalary}>{item.salary}</Text> : null}
            <Text style={st.cardDate}>Applied: {fmtDate(item.appliedDate)}</Text>
          </View>
          {item.matchScore > 0 && (
            <View style={st.matchPill}>
              <Ionicons name="star" size={11} color="#F59E0B"/>
              <Text style={st.matchT}>{item.matchScore}%</Text>
            </View>
          )}
          <TouchableOpacity style={st.viewBtn} onPress={()=>{setSelectedApp(item);setShowDetail(true);}}>
            <Text style={st.viewBtnT}>View</Text>
            <Ionicons name="chevron-forward" size={14} color="#0e4e96"/>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  }, []);

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: DETAIL MODAL
  // ═════════════════════════════════════════════════════════════════════════════
  const InfoRow = ({label,value,link}) => {
    if (!value) return null;
    return (
      <View style={st.infoRow}>
        <Text style={st.infoLabel}>{label}</Text>
        {link ? (
          <TouchableOpacity onPress={()=>Linking.openURL(link).catch(()=>{})}>
            <Text style={[st.infoVal,{color:'#0e4e96',textDecorationLine:'underline'}]}>{value}</Text>
          </TouchableOpacity>
        ) : <Text style={st.infoVal}>{value}</Text>}
      </View>
    );
  };

  const renderDetailModal = () => {
    if (!selectedApp) return null;
    const cfg = sc(selectedApp.status);
    const canWithdraw = !['withdrawn','rejected','hired','draft'].includes(selectedApp.status);
    const isDraft = selectedApp.status === 'draft';

    return (
      <Modal visible={showDetail} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowDetail(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
          <View style={st.modalHdr}>
            <TouchableOpacity onPress={()=>setShowDetail(false)}><Ionicons name="close" size={24} color="#1F2937"/></TouchableOpacity>
            <Text style={{fontSize:17,fontWeight:'700',color:'#1F2937'}}>Application Details</Text>
            <TouchableOpacity onPress={()=>shareApp(selectedApp)}><Ionicons name="share-social-outline" size={22} color="#64748B"/></TouchableOpacity>
          </View>

          <ScrollView style={{flex:1,padding:20}} showsVerticalScrollIndicator={false}>
            {/* Job Header */}
            <View style={{flexDirection:'row',marginBottom:20,gap:12}}>
              <View style={[st.cardLogo,{backgroundColor:cfg.bg,width:56,height:56,borderRadius:14}]}>
                <Text style={{fontSize:22,fontWeight:'800',color:cfg.color}}>{(selectedApp.company||'?')[0]}</Text>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:18,fontWeight:'800',color:'#1F2937'}}>{selectedApp.jobTitle}</Text>
                <Text style={{fontSize:14,color:'#0e4e96',fontWeight:'600',marginTop:2}}>{selectedApp.company}</Text>
                <Text style={{fontSize:13,color:'#64748B',marginTop:2}}>{selectedApp.location} • {selectedApp.jobType}</Text>
              </View>
            </View>

            {/* Status Card */}
            <View style={[st.detailStatusCard,{borderLeftColor:cfg.color}]}>
              <View style={[st.detailStatusIcon,{backgroundColor:cfg.bg}]}>
                <Ionicons name={cfg.icon} size={24} color={cfg.color}/>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:16,fontWeight:'700',color:cfg.color}}>{cfg.label}</Text>
                <Text style={{fontSize:12,color:'#64748B',marginTop:2}}>Progress: {cfg.progress}%</Text>
              </View>
            </View>

            {/* Progress Bar */}
            <View style={{marginBottom:20}}>
              <View style={[st.progressBar,{height:8}]}>
                <View style={[st.progressFill,{height:8,width:`${cfg.progress}%`,backgroundColor:cfg.color}]}/>
              </View>
            </View>

            {/* Details */}
            <View style={st.detailSection}>
              <Text style={st.detailSectionTitle}>Application Info</Text>
              <InfoRow label="Applied" value={fmtDateTime(selectedApp.appliedDate)}/>
              <InfoRow label="Last Updated" value={selectedApp.lastUpdated ? fmtDateTime(selectedApp.lastUpdated) : '—'}/>
              <InfoRow label="Salary" value={selectedApp.salary}/>
              <InfoRow label="Match Score" value={selectedApp.matchScore>0?`${selectedApp.matchScore}%`:null}/>
              <InfoRow label="Stage" value={selectedApp.stage}/>
            </View>

            {/* Interview */}
            {selectedApp.interviewDate && (
              <View style={st.detailSection}>
                <Text style={st.detailSectionTitle}>Interview</Text>
                <InfoRow label="Date" value={fmtDateTime(selectedApp.interviewDate)}/>
                <InfoRow label="Type" value={selectedApp.interviewType}/>
              </View>
            )}

            {/* Recruiter */}
            {(selectedApp.recruiterName||selectedApp.recruiterEmail) && (
              <View style={st.detailSection}>
                <Text style={st.detailSectionTitle}>Recruiter Contact</Text>
                <InfoRow label="Name" value={selectedApp.recruiterName}/>
                <InfoRow label="Email" value={selectedApp.recruiterEmail} link={selectedApp.recruiterEmail?`mailto:${selectedApp.recruiterEmail}`:null}/>
                <InfoRow label="Phone" value={selectedApp.recruiterPhone} link={selectedApp.recruiterPhone?`tel:${selectedApp.recruiterPhone}`:null}/>
              </View>
            )}

            {/* Status History */}
            {selectedApp.statusHistory?.length>0 && (
              <View style={st.detailSection}>
                <Text style={st.detailSectionTitle}>Status History</Text>
                {selectedApp.statusHistory.map((h,i)=>(
                  <View key={i} style={{flexDirection:'row',marginBottom:12}}>
                    <View style={{alignItems:'center',marginRight:12}}>
                      <View style={{width:8,height:8,borderRadius:4,backgroundColor:'#0e4e96',marginTop:6}}/>
                      {i<selectedApp.statusHistory.length-1&&<View style={{width:2,flex:1,backgroundColor:'#E2E8F0',marginTop:2}}/>}
                    </View>
                    <View style={{flex:1}}>
                      <Text style={{fontSize:13,fontWeight:'600',color:'#1F2937'}}>{sc(h.status).label}</Text>
                      <Text style={{fontSize:11,color:'#94A3B8',marginTop:1}}>{fmtDateTime(h.date)}</Text>
                      {h.note&&<Text style={{fontSize:12,color:'#64748B',fontStyle:'italic',marginTop:2}}>{h.note}</Text>}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </ScrollView>

          {/* Actions */}
          <View style={{padding:16,borderTopWidth:1,borderTopColor:'#E2E8F0',gap:10}}>
            {isDraft && (
              <TouchableOpacity style={st.primaryBtn} onPress={()=>{setShowDetail(false);navigation.navigate('JobApplication',{jobId:selectedApp.jobId,job:selectedApp});}}>
                <Text style={st.primaryBtnT}>Continue Application</Text>
                <Ionicons name="arrow-forward" size={18} color="#FFF"/>
              </TouchableOpacity>
            )}
            {!isDraft && (
              <TouchableOpacity style={st.primaryBtn} onPress={()=>{setShowDetail(false);navigation.navigate('ApplicationsScreen',{applicationId:selectedApp.id});}}>
                <Text style={st.primaryBtnT}>Track Application</Text>
                <Ionicons name="chevron-forward" size={18} color="#FFF"/>
              </TouchableOpacity>
            )}
            <View style={{flexDirection:'row',gap:10}}>
              {canWithdraw && (
                <TouchableOpacity style={[st.outlineBtn,{flex:1,borderColor:'#FCA5A5'}]} onPress={()=>withdrawApp(selectedApp)}>
                  <Ionicons name="close-circle-outline" size={16} color="#EF4444"/>
                  <Text style={[st.outlineBtnT,{color:'#EF4444'}]}>Withdraw</Text>
                </TouchableOpacity>
              )}
              {isDraft && (
                <TouchableOpacity style={[st.outlineBtn,{flex:1,borderColor:'#FCA5A5'}]} onPress={()=>deleteApp(selectedApp)}>
                  <Ionicons name="trash-outline" size={16} color="#EF4444"/>
                  <Text style={[st.outlineBtnT,{color:'#EF4444'}]}>Delete</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[st.outlineBtn,{flex:1}]} onPress={()=>shareApp(selectedApp)}>
                <Ionicons name="share-social-outline" size={16} color="#0e4e96"/>
                <Text style={st.outlineBtnT}>Share</Text>
              </TouchableOpacity>
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: SORT MODAL
  // ═════════════════════════════════════════════════════════════════════════════
  const renderSortModal = () => (
    <Modal visible={showSortModal} animationType="slide" transparent onRequestClose={()=>setShowSortModal(false)}>
      <TouchableOpacity style={{flex:1,backgroundColor:'rgba(0,0,0,0.4)'}} activeOpacity={1} onPress={()=>setShowSortModal(false)}>
        <View style={{flex:1}}/>
        <View style={{backgroundColor:'#FFF',borderTopLeftRadius:20,borderTopRightRadius:20,padding:20}}>
          <Text style={{fontSize:17,fontWeight:'700',color:'#1F2937',marginBottom:16}}>Sort By</Text>
          {SORT_OPTS.map(opt=>(
            <TouchableOpacity key={opt.key} style={st.sortRow} onPress={()=>{setSortBy(opt.key);setShowSortModal(false);}}>
              <Ionicons name={opt.icon} size={20} color={sortBy===opt.key?'#0e4e96':'#64748B'}/>
              <Text style={[st.sortRowT,sortBy===opt.key&&{color:'#0e4e96',fontWeight:'700'}]}>{opt.label}</Text>
              {sortBy===opt.key&&<Ionicons name="checkmark-circle" size={20} color="#0e4e96"/>}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // LOADING / ERROR
  // ═════════════════════════════════════════════════════════════════════════════
  if (isLoading) {
    return (
      <View style={{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F8FAFC'}}>
        <ActivityIndicator size={36} color="#0e4e96"/>
        <Text style={{marginTop:12,color:'#64748B'}}>Loading applications...</Text>
      </View>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <SafeAreaView style={{flex:1,backgroundColor:'#F8FAFC'}}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>

      {/* Header */}
      <LinearGradient colors={['#0e4e96','#1e7be2']} style={st.header}>
        <TouchableOpacity style={st.hdrBtn} onPress={()=>navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#FFF"/>
        </TouchableOpacity>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={st.hdrTitle}>My Applications</Text>
          <Text style={st.hdrSub}>{filtered.length} application{filtered.length!==1?'s':''}</Text>
        </View>
        <TouchableOpacity style={st.hdrBtn} onPress={()=>setShowSortModal(true)}>
          <Ionicons name="swap-vertical-outline" size={20} color="#FFF"/>
        </TouchableOpacity>
      </LinearGradient>

      {/* Stats */}
      {renderStats()}

      {/* Search */}
      <View style={st.searchWrap}>
        <View style={st.searchBar}>
          <Ionicons name="search" size={18} color="#94A3B8"/>
          <TextInput style={st.searchIn} placeholder="Search by title, company, location..." value={searchQuery}
            onChangeText={setSearchQuery} placeholderTextColor="#94A3B8"/>
          {searchQuery.length>0&&<TouchableOpacity onPress={()=>setSearchQuery('')}><Ionicons name="close-circle" size={18} color="#94A3B8"/></TouchableOpacity>}
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={st.filterRow}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:16,gap:8}}>
          {FILTER_TABS.map(tab=>{
            const count = tab.key==='all'?applications.length
              :tab.key==='active'?stats.active
              :tab.key==='interviews'?stats.interviews
              :tab.key==='offers'?stats.offers
              :stats.rejected+applications.filter(a=>['withdrawn','hired'].includes(a.status)).length;
            return (
              <TouchableOpacity key={tab.key} style={[st.filterChip, filterType===tab.key&&st.filterChipActive]}
                onPress={()=>setFilterType(tab.key)}>
                <Text style={[st.filterChipT, filterType===tab.key&&{color:'#FFF'}]}>{tab.label}</Text>
                <View style={[st.filterCount, filterType===tab.key&&{backgroundColor:'rgba(255,255,255,0.3)'}]}>
                  <Text style={[st.filterCountT, filterType===tab.key&&{color:'#FFF'}]}>{count}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Error Banner */}
      {error && (
        <TouchableOpacity style={st.errorBanner} onPress={()=>{setError(null);onRefresh();}}>
          <Ionicons name="alert-circle" size={16} color="#EF4444"/>
          <Text style={{fontSize:12,color:'#EF4444',flex:1,marginLeft:6}}>{error}</Text>
          <Ionicons name="refresh" size={14} color="#EF4444"/>
        </TouchableOpacity>
      )}

      {/* List */}
      {filtered.length===0 ? (
        <View style={{flex:1,justifyContent:'center',alignItems:'center',paddingHorizontal:40}}>
          <Ionicons name="briefcase-outline" size={56} color="#CBD5E1"/>
          <Text style={{fontSize:18,fontWeight:'700',color:'#1F2937',marginTop:14,textAlign:'center'}}>
            {searchQuery||filterType!=='all'?'No Matching Applications':'No Applications Yet'}
          </Text>
          <Text style={{fontSize:13,color:'#64748B',textAlign:'center',marginTop:6}}>
            {searchQuery||filterType!=='all'?'Try adjusting your search or filters':'Start applying for jobs to track them here'}
          </Text>
          {!searchQuery&&filterType==='all'&&(
            <TouchableOpacity style={[st.primaryBtn,{marginTop:20,paddingHorizontal:24}]}
              onPress={()=>navigation.navigate('FindJobs')}>
              <Ionicons name="search" size={18} color="#FFF"/>
              <Text style={st.primaryBtnT}>Find Jobs</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <FlatList
          data={filtered}
          renderItem={renderCard}
          keyExtractor={item=>item.id}
          contentContainerStyle={{padding:16,paddingBottom:30}}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} tintColor="#0e4e96"/>}
          showsVerticalScrollIndicator={false}
        />
      )}

      {renderDetailModal()}
      {renderSortModal()}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  header:      {flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14},
  hdrBtn:      {width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center'},
  hdrTitle:    {fontSize:20,fontWeight:'700',color:'#FFF'},
  hdrSub:      {fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:1},

  statsRow:    {flexDirection:'row',paddingHorizontal:12,paddingVertical:10,gap:6},
  statCard:    {flex:1,backgroundColor:'#FFF',borderRadius:12,paddingVertical:10,alignItems:'center',shadowColor:'#000',shadowOpacity:0.05,shadowRadius:4,elevation:1},
  statNum:     {fontSize:18,fontWeight:'900'},
  statLbl:     {fontSize:9,color:'#64748B',fontWeight:'500',marginTop:1},

  searchWrap:  {paddingHorizontal:16,paddingVertical:8},
  searchBar:   {flexDirection:'row',alignItems:'center',backgroundColor:'#FFF',borderRadius:12,paddingHorizontal:12,paddingVertical:Platform.OS==='ios'?12:8,gap:8,borderWidth:1,borderColor:'#E2E8F0'},
  searchIn:    {flex:1,fontSize:14,color:'#1F2937',padding:0},

  filterRow:   {paddingVertical:8},
  filterChip:  {flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:8,borderRadius:20,backgroundColor:'#FFF',borderWidth:1,borderColor:'#E2E8F0',gap:6},
  filterChipActive:{backgroundColor:'#0e4e96',borderColor:'#0e4e96'},
  filterChipT: {fontSize:13,fontWeight:'600',color:'#475569'},
  filterCount: {backgroundColor:'#F1F5F9',borderRadius:10,minWidth:20,height:20,justifyContent:'center',alignItems:'center',paddingHorizontal:4},
  filterCountT:{fontSize:10,fontWeight:'700',color:'#64748B'},

  card:        {backgroundColor:'#FFF',borderRadius:14,padding:14,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0',shadowColor:'#000',shadowOpacity:0.04,shadowRadius:6,elevation:1},
  cardTop:     {flexDirection:'row',alignItems:'flex-start',gap:10,marginBottom:10},
  cardLogo:    {width:42,height:42,borderRadius:12,justifyContent:'center',alignItems:'center'},
  cardTitle:   {fontSize:15,fontWeight:'700',color:'#1F2937'},
  cardCo:      {fontSize:13,color:'#0e4e96',fontWeight:'600',marginTop:1},
  cardMeta:    {fontSize:11,color:'#94A3B8'},
  remotePill:  {backgroundColor:'#D1FAE5',borderRadius:6,paddingHorizontal:5,paddingVertical:1,marginLeft:4},
  remotePillT: {fontSize:9,fontWeight:'700',color:'#059669'},
  statusBadge: {flexDirection:'row',alignItems:'center',gap:4,paddingHorizontal:8,paddingVertical:4,borderRadius:8,alignSelf:'flex-start'},
  statusBadgeT:{fontSize:10,fontWeight:'700'},
  progressRow: {flexDirection:'row',alignItems:'center',marginBottom:10,gap:8},
  progressBar: {flex:1,height:5,backgroundColor:'#F1F5F9',borderRadius:3,overflow:'hidden'},
  progressFill:{height:5,borderRadius:3},
  progressPct: {fontSize:11,fontWeight:'700',minWidth:28,textAlign:'right'},
  cardFoot:    {flexDirection:'row',alignItems:'center'},
  cardSalary:  {fontSize:13,fontWeight:'700',color:'#10B981'},
  cardDate:    {fontSize:11,color:'#94A3B8',marginTop:1},
  matchPill:   {flexDirection:'row',alignItems:'center',gap:3,backgroundColor:'#FEF3C7',borderRadius:8,paddingHorizontal:6,paddingVertical:3,marginRight:8},
  matchT:      {fontSize:10,fontWeight:'700',color:'#D97706'},
  viewBtn:     {flexDirection:'row',alignItems:'center',gap:2,paddingHorizontal:12,paddingVertical:6,borderRadius:8,borderWidth:1,borderColor:'#0e4e96'},
  viewBtnT:    {fontSize:12,fontWeight:'600',color:'#0e4e96'},

  modalHdr:    {flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E2E8F0'},
  detailStatusCard:{flexDirection:'row',alignItems:'center',backgroundColor:'#F8FAFC',borderRadius:12,padding:14,marginBottom:16,borderLeftWidth:4,gap:12},
  detailStatusIcon:{width:48,height:48,borderRadius:14,justifyContent:'center',alignItems:'center'},
  detailSection:{marginBottom:20},
  detailSectionTitle:{fontSize:15,fontWeight:'700',color:'#1F2937',marginBottom:10},
  infoRow:     {flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#F1F5F9'},
  infoLabel:   {fontSize:13,color:'#64748B',fontWeight:'500'},
  infoVal:     {fontSize:13,color:'#1F2937',fontWeight:'600',textAlign:'right',flex:1,marginLeft:16},

  primaryBtn:  {flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#0e4e96',paddingVertical:14,borderRadius:12,gap:8},
  primaryBtnT: {fontSize:15,fontWeight:'700',color:'#FFF'},
  outlineBtn:  {flexDirection:'row',alignItems:'center',justifyContent:'center',paddingVertical:10,borderRadius:10,borderWidth:1,borderColor:'#E2E8F0',gap:6},
  outlineBtnT: {fontSize:13,fontWeight:'600',color:'#0e4e96'},

  sortRow:     {flexDirection:'row',alignItems:'center',paddingVertical:14,borderBottomWidth:1,borderBottomColor:'#F1F5F9',gap:12},
  sortRowT:    {flex:1,fontSize:14,color:'#475569',fontWeight:'500'},

  errorBanner: {flexDirection:'row',alignItems:'center',marginHorizontal:16,marginBottom:8,backgroundColor:'#FEE2E2',borderRadius:10,padding:10},
});
